import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Wallet, Plus, CheckCircle2, XCircle, Trash2, Pencil, BarChart3, AlertTriangle, ShieldCheck,
} from "lucide-react";
import { api, idr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import LoadingState from "@/components/ui/loading-state";
import InputRupiah from "@/components/InputRupiah";
import { MonthPicker } from "@/components/ui/date-picker";
import { formatBulan, bulanIniISO } from "@/lib/tanggal";
import { useCabangOptions } from "@/lib/cabang";
import {
  PageHeader, TidakPunyaAkses, SearchInput, StatusBadge, SortTh, useSortState, sortRows,
  usePaginasi, Paginasi, Modal, Field, inputCls, selectCls, textareaCls, InputCabang,
  usePinHapus, PinHapusDialog, useAkunKasBank,
} from "./_shared";

// Bucket alokasi — SAMA persis dengan `BUDGET_BUCKETS` di backend/routers/marketing/budget.py.
// Duplikasi kecil yang disengaja (hanya label tampilan), bukan alpa — lihat pola yang sama di
// budget.py/expense.py sendiri (EXPENSE_CATEGORY_TO_BUCKET dikembar demi berdiri sendiri per berkas).
const BUCKETS = ["kol", "ads", "pfp", "production", "other"];
const BUCKET_LABEL = {
  kol: "KOL / Endorsement", ads: "Iklan Digital (Ads)", pfp: "PFP / Paid Promote",
  production: "Produksi Konten", other: "Lainnya",
};

const BUDGET_STATUS_META = {
  pending_approval: { label: "Menunggu Approval CEO", chip: "bg-amber-100 text-amber-800" },
  approved: { label: "Disetujui", chip: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Ditolak", chip: "bg-rose-100 text-rose-700" },
};

const ALERT_STYLE = {
  hijau: { bar: "bg-emerald-500", text: "text-emerald-700", card: "border-emerald-200 bg-emerald-50/50" },
  kuning: { bar: "bg-amber-500", text: "text-amber-700", card: "border-amber-300 bg-amber-50" },
  merah: { bar: "bg-rose-500", text: "text-rose-700", card: "border-rose-300 bg-rose-50" },
};

/** Role CEO (finance_ceo) + admin/superadmin — SAMA PERSIS `_bisa_approve_budget()`
 *  di backend/routers/marketing/budget.py. Approve budget SENGAJA cuma CEO,
 *  beda dari approval 2-tingkat modul Marketing lain (lihat docstring budget.py). */
function bisaApproveBudget(user) {
  const role = (user?.role || "").toLowerCase();
  return role === "finance_ceo" || role === "admin" || role === "superadmin";
}

/** Reject boleh SPV+ — mirror `bisa_approve_marketing()` di _shared.py backend
 *  (role admin/superadmin, role mengandung "spv", atau pemegang izin `.update`). */
function bisaTolakBudget(user, can) {
  const role = (user?.role || "").toLowerCase();
  if (role === "admin" || role === "superadmin") return true;
  if (role.includes("spv")) return true;
  return can("marketing_budget", "update");
}

const emptyForm = () => ({
  brand: "", period: bulanIniISO(), amount_total: 0,
  alokasi: { kol: 0, ads: 0, pfp: 0, production: 0, other: 0 },
  catatan: "", petty_akun_id: "",
});

export default function MarketingBudget() {
  const { user, can } = useAuth();
  const bolehLihat = can("marketing_budget", "view");
  const bolehTambah = can("marketing_budget", "create");
  const bolehHapus = can("marketing_budget", "delete");
  const bolehEdit = can("marketing_budget", "update");
  const bolehApprove = bisaApproveBudget(user);
  const bolehTolak = bisaTolakBudget(user, can);
  const brandOpsi = useCabangOptions({ denganKosong: true });
  // GET terbuka untuk siapa pun yang login — dipakai HANYA untuk memilih akun
  // kas kecil yang ditautkan (pelaporan), bukan untuk membayar apa pun di sini.
  const akunKasBank = useAkunKasBank();
  const akunPettyCash = useMemo(() => akunKasBank.filter((a) => a.category === "petty_cash"), [akunKasBank]);

  const [q, setQ] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const { sortKey, sortDir, toggle } = useSortState("created_at", "desc");
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const { dialog: pinDialog, setDialog: setPinDialog, hapusDenganPin, submitDialog } = usePinHapus();

  // Panel "Cek Burn Rate" — independen dari filter tabel supaya bisa melihat burn
  // rate brand/periode yang BELUM punya baris budget sekalipun (ada_budget: false).
  const [brandCek, setBrandCek] = useState("");
  const [periodeCek, setPeriodeCek] = useState(bulanIniISO());

  const { data, loading, error, refetch } = useDataQuery(
    ["marketing", "budget", brandFilter, statusFilter],
    () => api.get("/marketing/budget", {
      params: { ...(brandFilter ? { brand: brandFilter } : {}), ...(statusFilter ? { status: statusFilter } : {}) },
    }).then((r) => r.data.items),
    { enabled: bolehLihat },
  );

  const { data: burn, loading: loadingBurn, error: errorBurn, refetch: refetchBurn } = useDataQuery(
    ["marketing", "budget-burn-rate", brandCek, periodeCek],
    () => api.get("/marketing/budget/burn-rate", { params: { brand: brandCek, period: periodeCek } }).then((r) => r.data),
    { enabled: bolehLihat && !!brandCek && !!periodeCek },
  );

  const filtered = useMemo(() => {
    let out = data || [];
    const t = q.trim().toLowerCase();
    if (t) out = out.filter((b) => b.brand?.toLowerCase().includes(t) || b.code?.toLowerCase().includes(t));
    return out;
  }, [data, q]);
  const sorted = sortRows(filtered, sortKey, sortDir);
  const { page, setPage, totalPages, awal, akhir } = usePaginasi(sorted.length, 15);
  const halaman = sorted.slice(awal, akhir);

  const reload = () => refetch();

  const lihatBurnRate = (b) => { setBrandCek(b.brand); setPeriodeCek(b.period); };

  const bukaTambah = () => { setEditId(null); setForm(emptyForm()); };
  const bukaEdit = (b) => { setEditId(b.id); setForm({ brand: b.brand, period: b.period, amount_total: b.amount_total, alokasi: { ...b.alokasi }, catatan: b.catatan || "", petty_akun_id: b.petty_akun_id || "", __status: b.status }); };

  const totalAlokasiForm = form ? BUCKETS.reduce((s, k) => s + Number(form.alokasi[k] || 0), 0) : 0;

  const simpan = async () => {
    if (!form.brand.trim()) { toast.error("Brand wajib diisi"); return; }
    if (!form.period) { toast.error("Periode wajib diisi"); return; }
    if (!form.amount_total || form.amount_total <= 0) { toast.error("Total anggaran wajib lebih dari 0"); return; }
    if (totalAlokasiForm > form.amount_total) { toast.error("Total alokasi kategori melebihi total anggaran"); return; }
    setBusy(true);
    try {
      const { __status, ...payload } = form;
      if (editId) {
        const r = await api.patch(`/marketing/budget/${editId}`, payload);
        toast.success(r.data.message || "Anggaran diperbarui");
      } else {
        const r = await api.post("/marketing/budget", payload);
        toast.success(r.data.message || "Anggaran diajukan");
      }
      setForm(null); setEditId(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan anggaran"); }
    finally { setBusy(false); }
  };

  const setujui = async (b) => {
    if (!window.confirm(`Setujui anggaran ${b.brand} periode ${formatBulan(b.period)} (${idr(b.amount_total)})?`)) return;
    try {
      const r = await api.post(`/marketing/budget/${b.id}/approve`);
      toast.success(r.data.message || "Anggaran disetujui");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyetujui anggaran"); }
  };

  const tolak = async (b) => {
    const alasan = window.prompt(`Alasan menolak anggaran ${b.brand} ${formatBulan(b.period)}?`) || "";
    if (!alasan.trim()) return;
    try {
      const r = await api.post(`/marketing/budget/${b.id}/reject`, { alasan });
      toast.success(r.data.message || "Anggaran ditolak");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menolak anggaran"); }
  };

  const hapus = async (b) => {
    if (!window.confirm(`Hapus anggaran ${b.brand} periode ${formatBulan(b.period)}?`)) return;
    const aksi = (pin) => api.delete(`/marketing/budget/${b.id}`, pin ? { params: { pin } } : undefined);
    const langsung = await hapusDenganPin(aksi, { modul: "marketing_budget", targetId: b.id, ringkasan: `Hapus anggaran ${b.brand} ${b.period}` });
    if (langsung) { toast.success("Anggaran dihapus"); reload(); }
  };

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 space-y-5">
        <PageHeader icon={Wallet} title="Budget Control" />
        <TidakPunyaAkses label="Budget Control Marketing" testid="mkt-budget-tanpa-akses" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-budget">
      <PageHeader
        icon={Wallet} title="Budget Control"
        desc="Anggaran marketing per brand per bulan, dengan alokasi per kategori (KOL/Ads/PFP/Produksi/Lain) dan pantauan burn rate real-time."
        actions={bolehTambah && (
          <button onClick={bukaTambah} data-testid="mkt-budget-tambah"
                  className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Ajukan Anggaran
          </button>
        )}
      />

      {/* Panel Cek Burn Rate — visual mencolok untuk bucket yang mendekati/lewat batas */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3" data-testid="mkt-budget-burn-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
            <BarChart3 className="w-4 h-4 text-emerald-600" /> Cek Burn Rate
          </p>
          <div className="flex items-center gap-2">
            <InputCabang value={brandCek} onChange={setBrandCek} opsi={brandOpsi} testid="mkt-budget-cek-brand"
                         placeholder="Pilih brand" className={`${inputCls} h-9 w-48`} />
            <MonthPicker value={periodeCek} onChange={setPeriodeCek} data-testid="mkt-budget-cek-periode" className="w-36" />
          </div>
        </div>

        {!brandCek ? (
          <p className="text-sm text-slate-400 py-4 text-center">Pilih brand untuk melihat realisasi vs alokasi anggaran.</p>
        ) : (
          <LoadingState loading={loadingBurn} error={errorBurn} onRetry={refetchBurn} varian="kartu" jumlahKartu={3} data-testid="mkt-budget-burn-loading">
            {burn && !burn.ada_budget && (
              <p className="text-sm text-slate-500 py-3 px-1">
                Belum ada anggaran disetujui untuk <b>{brandCek}</b> periode {formatBulan(periodeCek)} — realisasi sejauh ini
                <b> {idr(burn.grand_total)}</b> (dari expense yang sudah disetujui/dibayar), tapi tidak ada batas anggaran untuk dibandingkan.
              </p>
            )}
            {burn && (
              <>
                {/* Total */}
                <div className={`rounded-lg border p-3 ${burn.amount_total > 0 ? (ALERT_STYLE[burn.total_alert]?.card || "") : "border-slate-200"}`} data-testid="mkt-budget-burn-total">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs font-semibold text-slate-700">Total {brandCek} — {formatBulan(periodeCek)}</p>
                    <p className={`text-sm font-mono font-bold ${burn.amount_total > 0 ? (ALERT_STYLE[burn.total_alert]?.text || "text-slate-700") : "text-slate-700"}`}>
                      {idr(burn.grand_total)} {burn.amount_total > 0 && `/ ${idr(burn.amount_total)}`}
                      {burn.total_pct_used !== null && <span className="ml-1.5">({burn.total_pct_used}%)</span>}
                    </p>
                  </div>
                  {burn.amount_total > 0 && (
                    <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full ${ALERT_STYLE[burn.total_alert]?.bar || "bg-slate-400"}`} style={{ width: `${Math.min(100, burn.total_pct_used || 0)}%` }} />
                    </div>
                  )}
                  {burn.total_alert === "merah" && (
                    <p className="text-[11px] text-rose-700 mt-1.5 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Total anggaran brand ini sudah TERLAMPAUI.</p>
                  )}
                </div>

                {/* Kas Kecil — KARTU TERPISAH dari Total di atas, SENGAJA tidak
                    digabung: sisa anggaran = rencana, saldo kas kecil = uang
                    fisik di laci saat ini. Lihat backend budget.py docstring
                    "TAUTAN KE AKUN KAS KECIL". Hanya tampil kalau budget ini
                    ditaut ke sebuah akun kas kecil. */}
                {burn.kas_kecil && (
                  <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3" data-testid="mkt-budget-burn-kaskecil">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-indigo-800">Kas Kecil: {burn.kas_kecil.akun_nama}</p>
                      <p className="text-sm font-mono font-bold text-indigo-800">{idr(burn.kas_kecil.saldo)}</p>
                    </div>
                    <p className="text-[10px] text-indigo-700">{burn.kas_kecil.catatan}</p>
                  </div>
                )}

                {/* Per bucket */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  {burn.per_bucket.map((b) => {
                    const st = ALERT_STYLE[b.alert] || ALERT_STYLE.hijau;
                    return (
                      <div key={b.bucket} className={`rounded-lg border p-3 ${b.alokasi > 0 ? st.card : "border-slate-200"}`} data-testid={`mkt-budget-burn-bucket-${b.bucket}`}>
                        <p className="text-[11px] font-semibold text-slate-600">{BUCKET_LABEL[b.bucket]}</p>
                        <p className={`text-sm font-mono font-bold mt-0.5 ${b.alokasi > 0 ? st.text : "text-slate-700"}`}>{idr(b.actual)}</p>
                        <p className="text-[10px] text-slate-400">dari {idr(b.alokasi)}</p>
                        {b.alokasi > 0 && (
                          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1.5">
                            <div className={`h-full ${st.bar}`} style={{ width: `${Math.min(100, b.pct_used || 0)}%` }} />
                          </div>
                        )}
                        {b.pct_used !== null && <p className={`text-[10px] font-semibold mt-1 ${st.text}`}>{b.pct_used}%{b.alert === "merah" ? " — OVER" : b.alert === "kuning" ? " — mendekati batas" : ""}</p>}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </LoadingState>
        )}
      </div>

      {/* Toolbar filter tabel */}
      <div className="flex items-center gap-2 flex-wrap">
        <SearchInput value={q} onChange={setQ} placeholder="Cari brand/kode..." testid="mkt-budget-cari" className="w-56" />
        <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} data-testid="mkt-budget-filter-brand"
                className={`${selectCls} h-9 w-auto`}>
          <option value="">Semua Brand</option>
          {brandOpsi.filter(Boolean).map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="mkt-budget-filter-status"
                className={`${selectCls} h-9 w-auto`}>
          <option value="">Semua Status</option>
          {Object.entries(BUDGET_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <SortTh label="Kode / Brand" sortKeyName="brand" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-budget-sort-brand" />
                <SortTh label="Periode" sortKeyName="period" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-budget-sort-periode" />
                <SortTh label="Total Anggaran" sortKeyName="amount_total" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="mkt-budget-sort-total" />
                <th className="px-3 py-2.5 font-semibold">Status</th>
                <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <LoadingState loading={loading} error={error} onRetry={refetch} kosong={!loading && !error && halaman.length === 0}
                            varian="tabel" kolom={5} pesanKosong="Belum ada anggaran yang diajukan." data-testid="mkt-budget-state">
                {halaman.map((b) => (
                  <tr key={b.id} className="hover:bg-slate-50/60" data-testid={`mkt-budget-row-${b.id}`}>
                    <td className="px-3 py-2.5">
                      <p className="font-mono text-[11px] text-slate-500">{b.code}</p>
                      <p className="text-slate-900 font-medium">{b.brand}</p>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">{formatBulan(b.period)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{idr(b.amount_total)}</td>
                    <td className="px-3 py-2.5"><StatusBadge meta={BUDGET_STATUS_META} value={b.status} /></td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        <button onClick={() => lihatBurnRate(b)} data-testid={`mkt-budget-burn-lihat-${b.id}`}
                                className="px-2 h-8 rounded border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs font-medium inline-flex items-center gap-1">
                          <BarChart3 className="w-3.5 h-3.5" /> Burn Rate
                        </button>
                        {b.status === "pending_approval" && bolehApprove && (
                          <button onClick={() => setujui(b)} data-testid={`mkt-budget-setujui-${b.id}`}
                                  title="Hanya CEO yang bisa menyetujui anggaran"
                                  className="px-2 h-8 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Setujui
                          </button>
                        )}
                        {b.status === "pending_approval" && bolehTolak && (
                          <button onClick={() => tolak(b)} data-testid={`mkt-budget-tolak-${b.id}`}
                                  className="px-2 h-8 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-medium inline-flex items-center gap-1">
                            <XCircle className="w-3.5 h-3.5" /> Tolak
                          </button>
                        )}
                        {/* PATCH selalu butuh izin .update; status "approved" TAMBAHAN butuh CEO
                            (mirror backend: require_permission("marketing_budget","update") DI LUAR,
                            lalu _bisa_approve_budget() DI DALAM khusus status approved). */}
                        {bolehEdit && (b.status === "pending_approval" || (b.status === "approved" && bolehApprove)) && (
                          <button onClick={() => bukaEdit(b)} data-testid={`mkt-budget-edit-${b.id}`}
                                  title={b.status === "approved" ? "Anggaran yang sudah disetujui hanya bisa direvisi CEO" : undefined}
                                  className="w-8 h-8 rounded hover:bg-slate-100 text-slate-500 grid place-items-center">
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        {["pending_approval", "rejected"].includes(b.status) && bolehHapus && (
                          <button onClick={() => hapus(b)} data-testid={`mkt-budget-hapus-${b.id}`}
                                  className="w-8 h-8 rounded hover:bg-rose-50 text-rose-600 grid place-items-center">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </LoadingState>
            </tbody>
          </table>
        </div>
        <Paginasi page={page} setPage={setPage} totalPages={totalPages} testid="mkt-budget-paginasi" />
      </div>

      {/* Form ajukan/edit anggaran */}
      <Modal open={!!form} onClose={() => { setForm(null); setEditId(null); }} title={editId ? "Edit Anggaran" : "Ajukan Anggaran Baru"}
             maxWidth="max-w-xl" testid="mkt-budget-modal"
             footer={<>
               <button onClick={() => { setForm(null); setEditId(null); }} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
               <button onClick={simpan} disabled={busy} data-testid="mkt-budget-simpan"
                       className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                 {busy ? "Menyimpan..." : editId ? "Simpan" : "Ajukan"}
               </button>
             </>}>
        {form && (
          <>
            {editId && form.__status === "approved" && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 inline-flex items-start gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Anggaran ini sudah disetujui — merevisinya akan mereset status notifikasi ambang 80%/100%.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Brand" required>
                <InputCabang value={form.brand} onChange={(v) => setForm((f) => ({ ...f, brand: v }))}
                             opsi={brandOpsi} testid="mkt-budget-f-brand" />
              </Field>
              <Field label="Periode (Bulan)" required>
                <MonthPicker value={form.period} onChange={(v) => setForm((f) => ({ ...f, period: v }))} data-testid="mkt-budget-f-periode" />
              </Field>
            </div>
            <Field label="Total Anggaran (Rp)" required>
              <InputRupiah value={form.amount_total} onChange={(v) => setForm((f) => ({ ...f, amount_total: v }))}
                           testid="mkt-budget-f-total" className={inputCls} />
            </Field>

            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-700">Alokasi per Kategori</p>
                <p className={`text-xs font-mono ${totalAlokasiForm > (form.amount_total || 0) ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
                  {idr(totalAlokasiForm)} / {idr(form.amount_total || 0)}
                </p>
              </div>
              <div className="space-y-2">
                {BUCKETS.map((k) => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="text-xs text-slate-600 w-40 shrink-0">{BUCKET_LABEL[k]}</span>
                    <InputRupiah value={form.alokasi[k]} testid={`mkt-budget-f-alokasi-${k}`}
                                 onChange={(v) => setForm((f) => ({ ...f, alokasi: { ...f.alokasi, [k]: v } }))}
                                 className={`${inputCls} h-9 flex-1`} />
                  </div>
                ))}
              </div>
              {totalAlokasiForm > (form.amount_total || 0) && (
                <p className="text-[11px] text-rose-600 mt-2">Total alokasi melebihi total anggaran — kurangi salah satu kategori.</p>
              )}
            </div>

            <Field label="Akun Kas Kecil (opsional)"
                   hint="Hanya untuk PELAPORAN — menaut anggaran ini ke saldo kas kecil Marketing supaya bisa dibandingkan. Sisa anggaran (rencana) dan saldo kas kecil (uang fisik) TETAP dua angka berbeda.">
              <select value={form.petty_akun_id} data-testid="mkt-budget-f-petty-akun" className={selectCls}
                      onChange={(e) => setForm((f) => ({ ...f, petty_akun_id: e.target.value }))}>
                <option value="">— tidak ditaut —</option>
                {akunPettyCash.map((a) => (
                  <option key={a.id} value={a.id}>{a.code} — {a.name} (saldo {idr(a.balance)})</option>
                ))}
              </select>
            </Field>

            <Field label="Catatan">
              <textarea value={form.catatan} rows={2} data-testid="mkt-budget-f-catatan"
                        onChange={(e) => setForm((f) => ({ ...f, catatan: e.target.value }))} className={textareaCls} />
            </Field>
          </>
        )}
      </Modal>

      <PinHapusDialog dialog={pinDialog} setDialog={setPinDialog} onSubmit={() => submitDialog(reload)} />
    </div>
  );
}
